import { CalendarIcon, CopyIcon, SectionCopyIcon } from '@/components/icons';
import { useNote } from '@/context/NoteProvider';
import { formatDate, formatTime } from '@/helpers/formatTime';
import { capitalizeWords } from '@/helpers/helper';
import { INote } from '@/interfaces/noteInterface';
import { alpha } from '@/styles/shared';
import { Box, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import {
  COPY_ANIMATION_DURATION,
  copyAllSections,
  copySection,
  transformFieldName,
} from './helpers';
import { noteDisplayStyles } from './styles';

interface NoteDisplayProps {
  noteToDisplay?: INote | null;
}

const NoteDisplay = ({ noteToDisplay }: NoteDisplayProps = {}) => {
  const noteContext = useNote();
  const displayNote = noteToDisplay || noteContext.currentNote;

  const [copyAnimating, setCopyAnimating] = useState(false);
  const [sectionCopyAnimating, setSectionCopyAnimating] = useState<
    string | null
  >(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);
  const copyAllTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const name = capitalizeWords(displayNote?.patient_name || '');

  const handleCopyAll = () => {
    // Clear existing timeout
    if (copyAllTimeoutRef.current) {
      clearTimeout(copyAllTimeoutRef.current);
    }

    setCopyAnimating(true);
    copyAllSections(displayNote || {});

    copyAllTimeoutRef.current = setTimeout(() => {
      setCopyAnimating(false);
      copyAllTimeoutRef.current = null;
    }, COPY_ANIMATION_DURATION);
  };

  const handleSectionCopy = (key: string, content: string) => {
    // Clear existing timeout
    if (sectionTimeoutRef.current) {
      clearTimeout(sectionTimeoutRef.current);
    }

    setSectionCopyAnimating(key);
    const transformedKey = transformFieldName(key);
    copySection(transformedKey, content);

    sectionTimeoutRef.current = setTimeout(() => {
      setSectionCopyAnimating(null);
      sectionTimeoutRef.current = null;
    }, COPY_ANIMATION_DURATION);
  };

  return (
    <Box sx={noteDisplayStyles.container}>
      {/* Header with date and title */}
      <Box sx={noteDisplayStyles.header}>
        {/* Date with calendar icon - hide when collapsed on mobile/tablet */}
        <Box
          onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
          sx={{
            ...noteDisplayStyles.dateContainer,
            '@media (max-width: 1024px)': {
              display: isHeaderExpanded ? 'flex' : 'none',
              cursor: 'pointer',
            },
          }}
        >
          <Box sx={noteDisplayStyles.calendarIcon}>
            <CalendarIcon />
          </Box>
          <Typography sx={noteDisplayStyles.dateText}>
            {displayNote?.created_on
              ? `${new Date(displayNote.created_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${formatTime(displayNote.created_on)}`
              : new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}
          </Typography>
        </Box>

        {/* Generated note title with toggle */}
        <Box
          onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isHeaderExpanded ? 'space-between' : 'flex-start',
            '@media (max-width: 1024px)': {
              marginBottom: isHeaderExpanded ? '0px' : '8px',
              cursor: 'pointer',
            },
          }}
        >
          <Typography
            sx={{
              ...noteDisplayStyles.title,
              '@media (max-width: 1024px)': {
                display: isHeaderExpanded ? 'block' : 'none',
              },
            }}
          >
            Generated note
          </Typography>
          <Box
            sx={{
              display: 'none',
              '@media (max-width: 1024px)': {
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '0px',
              },
            }}
          >
            {!isHeaderExpanded && (
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#000000',
                }}
              >
                Show details
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transform: isHeaderExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                marginLeft: '16px',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="#5B21B6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
          </Box>
        </Box>

        {/* Note info - collapsible on mobile/tablet */}
        <Box
          onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
          sx={{
            ...noteDisplayStyles.noteInfo,
            '@media (max-width: 1024px)': {
              display: isHeaderExpanded ? 'block' : 'none',
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
            },
          }}
        >
          Note{' '}
          {name ? (
            <>
              for <strong>{name}</strong>
            </>
          ) : (
            'for Unknown'
          )}{' '}
          has been initiated{' '}
          {displayNote?.created_on
            ? formatTime(displayNote.created_on)
            : '11:52 am'}
          {', '}
          {displayNote?.created_on
            ? formatDate(displayNote.created_on)
            : '12 Sept'}
        </Box>

        {/* Copy instruction and copy all button */}
        <Box
          sx={{
            ...noteDisplayStyles.copySection,
            '@media (max-width: 1024px)': {
              paddingTop: isHeaderExpanded
                ? noteDisplayStyles.copySection.paddingTop
                : '10px',
              marginTop: isHeaderExpanded
                ? noteDisplayStyles.copySection.marginTop
                : '0px',
            },
          }}
        >
          <Box sx={noteDisplayStyles.copyInstruction}>
            <Typography sx={noteDisplayStyles.copyInstructionText}>
              Click on icon
            </Typography>

            <Box sx={noteDisplayStyles.copyIcon}>
              <CopyIcon />
            </Box>

            <Typography sx={noteDisplayStyles.copyInstructionText}>
              to copy text
            </Typography>
          </Box>

          <Box onClick={handleCopyAll} sx={noteDisplayStyles.copyAllButton}>
            <Typography sx={noteDisplayStyles.copyAllText(copyAnimating)}>
              Copy all sections at once
            </Typography>

            <Box sx={noteDisplayStyles.copyAllIcon}>
              <CopyIcon fill={copyAnimating ? '#000000' : alpha.purple60} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Note content area */}
      <Box sx={noteDisplayStyles.contentArea}>
        {/* Note sections - show all existing sections including empty ones */}
        {displayNote &&
          Object.keys(displayNote)
            .filter(key => key.includes('Text'))
            .map((key: string) => {
              const sectionContent =
                displayNote[key as keyof typeof displayNote]?.toString() || '';
              return (
                <Box key={key} sx={noteDisplayStyles.sectionContainer}>
                  {/* Section header */}
                  <Box sx={noteDisplayStyles.sectionHeader}>
                    {/* Bullet point */}
                    <Box sx={noteDisplayStyles.bulletPoint} />

                    {/* Section title */}
                    <Typography sx={noteDisplayStyles.sectionTitle}>
                      {key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase())}
                    </Typography>

                    {/* Copy icon */}
                    <Box
                      sx={noteDisplayStyles.sectionCopyIcon}
                      onClick={() => handleSectionCopy(key, sectionContent)}
                    >
                      <SectionCopyIcon
                        fill={
                          sectionCopyAnimating === key
                            ? '#000000'
                            : alpha.purple60
                        }
                      />
                    </Box>
                  </Box>

                  {/* Section content */}
                  <Typography sx={noteDisplayStyles.sectionContent}>
                    {sectionContent || 'No content'}
                  </Typography>
                </Box>
              );
            })}
      </Box>
    </Box>
  );
};

export default NoteDisplay;
